import React, { useCallback, useRef, useState } from 'react';
import styles from './Tooltip.module.css';

const ESC_KEY = 'Escape';
const ENTER_KEY = 'Enter';

const Arrow = () => (
  <svg
    className={styles.tooltipArrow}
    height="5"
    preserveAspectRatio="none"
    viewBox="0 0 30 10"
    width="10"
  >
    <polygon points="0,0 30,0 15,10" />
  </svg>
);

/**
 * Accesible tooltip. Wraps the trigger element to show the tooltip
 * @param {*} props.children MUST be a single element
 * @returns {JSX.Element}
 */
export const Tooltip = ({
  children,
  content,
  containerClassName = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const tooltipId = useRef(`tooltip-${Math.random()
    .toString(36).substring(2, 9)}`);
  const animationTimeoutRef = useRef(null);

  const showTooltip = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    setIsAnimatingOut(false);
    setIsVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    if (isVisible) {
      setIsAnimatingOut(true);
      animationTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsAnimatingOut(false);
      }, 150);
    }
  }, [isVisible]);

  const triggerElement = React.cloneElement(children, {
    'aria-describedby': isVisible ? tooltipId.current : undefined,
    onBlur: (event) => {
      hideTooltip();
      children.props.onBlur?.(event);
    },
    onFocus: (event) => {
      showTooltip();
      children.props.onFocus?.(event);
    },
    onKeyDown: (event) => {
      if (event.key === ESC_KEY || event.key === ENTER_KEY) {
        hideTooltip();
      }
      children.props.onKeyDown?.(event);
    },
    onMouseEnter: (event) => {
      showTooltip();
      children.props.onMouseEnter?.(event);
    },
    onMouseLeave: (event) => {
      hideTooltip();
      children.props.onMouseLeave?.(event);
    },
  });

  return (
    <div className={`${styles.container} ${containerClassName}`}>
      {triggerElement}

      <div
        aria-hidden={isVisible}
        className={`${styles.tooltip} ${styles.tooltipTop} ${isAnimatingOut
          ? styles.animateOut
          : styles.animateIn}`}
        id={tooltipId.current}
        role="tooltip"
      >
        <p>
          {content}
        </p>

        <Arrow />
      </div>
    </div>
  );
};
